// src/components/LocationSelector.js
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";
import {
  MapPin,
  ArrowRight,
  ChevronDown,
  Compass,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";

// Authentic fallback data matching existing API branches
const FALLBACK_ADDRESSES = [
  {
    _id: "6871823bdffe2f15c8d0ae2a",
    addressId: "ADDRS00013",
    name: "Kondapur - Premium Theatres",
    phone: "9059382797",
    city: "Hyderabad",
    state: "Telangana",
    postalCode: "500084",
    landmark: "Asian Suncity Behind Sarath City Mall",
    addressLine1: "Asian Suncity, Behind Sarath City Mall, Kondapur",
  },
  {
    _id: "687181cbdffe2f15c8d0ae1d",
    addressId: "ADDRS00012",
    name: "Gachibowli",
    phone: "8341428342",
    city: "Hyderabad",
    state: "Telangana",
    postalCode: "500084",
    landmark: "Behind Indriya jewelers",
    addressLine1: "Behind Indriya Jewelers, Main Road, Gachibowli",
  },
  {
    _id: "68d2afe21aaafdc6fe7f16ba",
    addressId: "ADDRS00014",
    name: "Miyapur",
    phone: "8341428342",
    city: "Hyderabad",
    state: "Telangana",
    postalCode: "500050",
    landmark: "Beside Kalyan Jewellers",
    addressLine1: "3rd Floor Above Bajaj Bike showroom, Beside Kalyan Jewellers, Miyapur",
  },
];

export default function LocationSelector() {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState(FALLBACK_ADDRESSES);
  const [cities, setCities] = useState(["Hyderabad"]);
  const [selectedCity, setSelectedCity] = useState("Hyderabad");
  const [selectedBranchId, setSelectedBranchId] = useState("");

  useEffect(() => {
    let isMounted = true;
    axios
      .get("https://api.carnivalcastle.com/v1/carnivalApi/admin/address/alladdress")
      .then((res) => {
        if (isMounted && res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
          const data = res.data.data;
          const cleanedCities = [
            ...new Set(data.map((item) => (item.city || "Hyderabad").trim())),
          ];
          setAddresses(data);
          setCities(cleanedCities);
          if (cleanedCities.length > 0 && !cleanedCities.includes(selectedCity)) {
            setSelectedCity(cleanedCities[0]);
          }
        }
      })
      .catch((err) => {
        console.warn("Using local verified venue directory.", err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredAddresses = useMemo(() => {
    const list = addresses.filter(
      (a) => (a.city || "Hyderabad").trim().toLowerCase() === selectedCity.toLowerCase()
    );
    return list.length > 0 ? list : addresses;
  }, [addresses, selectedCity]);

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
    setSelectedBranchId("");
  };

  const handleBranchChange = (e) => {
    const branchId = e.target.value;
    setSelectedBranchId(branchId);
    const selected = filteredAddresses.find((a) => a._id === branchId);
    if (selected) {
      handleBookNow(selected);
    }
  };

  const handleBookNow = (address) => {
    if (!address) return;
    const slug = address.name.toLowerCase().replace(/\s+/g, "-");
    sessionStorage.setItem("selectedAddress", JSON.stringify(address));
    navigate(`/theaters/${slug}`, { state: { address } });
  };

  return (
    <>
      <Helmet>
        <title>Choose Your Nearest Location | Carnival Castle</title>
        <meta
          name="description"
          content="Select your nearest Carnival Castle private celebration theatre in Hyderabad: Gachibowli, Kondapur, and Miyapur."
        />
      </Helmet>

      <div className="loc-clean-root">
        <Header />

        {/* ── Compact Editorial Hero ── */}
        <section className="loc-clean-hero">
          <div className="loc-clean-container">
            <span className="loc-clean-eyebrow">✦ OUR PRIVATE THEATRES ✦</span>
            <h1 className="loc-clean-title">Find Your Celebration Venue</h1>
            <p className="loc-clean-desc">
              Choose the Carnival Castle theatre closest to you and begin planning your celebration.
            </p>
          </div>
        </section>

        {/* ── Main Content Area ── */}
        <main className="loc-clean-main">
          <div className="loc-clean-container">
            {/* ── Elegant Concierge Selector Card ── */}
            <div className="loc-clean-card">
              <div className="loc-card-accent-bar" />

              <div className="loc-card-header">
                <span className="loc-card-tag">✦ CONCIERGE SELECTOR ✦</span>
                <h2 className="loc-card-title">Select City & Nearest Branch</h2>
                <p className="loc-card-subtitle">
                  Pick your city, then choose a branch to explore theatre spaces and availability.
                </p>
              </div>

              <div className="loc-form-grid">
                {/* City Selection */}
                <div className="loc-field-group">
                  <label className="loc-field-label">
                    <MapPin size={13} className="loc-field-icon" /> City
                  </label>
                  <div className="loc-select-wrapper">
                    <select
                      className="loc-clean-select"
                      value={selectedCity}
                      onChange={handleCityChange}
                    >
                      {cities.map((city, i) => (
                        <option key={i} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="loc-select-arrow" />
                  </div>
                </div>

                {/* Branch Selection */}
                <div className="loc-field-group">
                  <label className="loc-field-label">
                    <Compass size={13} className="loc-field-icon" /> Nearest Branch
                  </label>
                  <div className="loc-select-wrapper">
                    <select
                      className="loc-clean-select"
                      value={selectedBranchId}
                      onChange={handleBranchChange}
                    >
                      <option value="">Choose a branch...</option>
                      {filteredAddresses.map((a, idx) => (
                        <option key={a._id} value={a._id}>
                          0{idx + 1} — {a.name} ({a.landmark || a.city})
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="loc-select-arrow" />
                  </div>
                </div>
              </div>

              {/* Quick Branch Selector Pills */}
              <div className="loc-quick-branches">
                <span className="loc-quick-label">Or Select Directly:</span>
                <div className="loc-pills-row">
                  {filteredAddresses.map((a, idx) => (
                    <button
                      key={a._id}
                      type="button"
                      className="loc-branch-pill"
                      onClick={() => handleBookNow(a)}
                    >
                      <span className="loc-pill-num">0{idx + 1}</span>
                      <span className="loc-pill-name">{a.name}</span>
                      <ArrowRight size={13} className="loc-pill-arrow" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </main>

        <Footer />
      </div>

      <style>{`
        /* ── Base Layout: Clean Warm Ivory (Zero Images, Zero Purple Gradient) ── */
        .loc-clean-root {
          background-color: #FAF7F2;
          color: #2D2633;
          min-height: 100vh;
        }

        .loc-clean-container {
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* ── Hero Section ── */
        .loc-clean-hero {
          background-color: #F5EFE6;
          border-bottom: 1px solid #E6DCCF;
          padding: 48px 20px 40px;
          text-align: center;
        }

        .loc-clean-eyebrow {
          display: inline-block;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2px;
          color: #B89535;
          text-transform: uppercase;
          margin-bottom: 10px;
        }

        .loc-clean-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(28px, 4vw, 42px);
          font-weight: 700;
          color: #1A0030;
          line-height: 1.18;
          margin: 0 0 10px;
        }

        .loc-clean-desc {
          font-size: 14.5px;
          color: #665D6F;
          line-height: 1.55;
          max-width: 580px;
          margin: 0 auto;
        }

        /* ── Main Section ── */
        .loc-clean-main {
          padding: 42px 0 64px;
        }

        /* ── Central Concierge Card ── */
        .loc-clean-card {
          background-color: #FFFFFF;
          border: 1px solid #E5DCD0;
          border-radius: 16px;
          padding: 32px 36px 30px;
          box-shadow: 0 10px 30px rgba(26, 0, 48, 0.04);
          max-width: 680px;
          margin: 0 auto;
          position: relative;
        }

        .loc-card-accent-bar {
          position: absolute;
          top: 0;
          left: 36px;
          right: 36px;
          height: 3px;
          background-color: #C9A84C;
          border-radius: 0 0 4px 4px;
        }

        .loc-card-header {
          text-align: center;
          margin-bottom: 24px;
        }

        .loc-card-tag {
          display: inline-block;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.5px;
          color: #B89535;
          text-transform: uppercase;
          margin-bottom: 6px;
        }

        .loc-card-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 22px;
          font-weight: 700;
          color: #1A0030;
          margin: 0 0 6px;
        }

        .loc-card-subtitle {
          font-size: 13px;
          color: #7A7082;
          margin: 0;
        }

        .loc-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
          margin-bottom: 22px;
        }

        .loc-field-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .loc-field-label {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          color: #554D5D;
          margin: 0;
        }

        .loc-field-icon {
          color: #B89535;
        }

        .loc-select-wrapper {
          position: relative;
        }

        .loc-clean-select {
          width: 100%;
          background-color: #FAF7F2;
          border: 1.5px solid #E2D7C7;
          border-radius: 8px;
          padding: 11px 36px 11px 14px;
          font-size: 13.5px;
          font-weight: 500;
          color: #1A0030;
          outline: none;
          cursor: pointer;
          appearance: none;
          -webkit-appearance: none;
          transition: border-color 0.2s ease, background-color 0.2s ease;
        }

        .loc-clean-select:focus {
          border-color: #5E0E73;
          background-color: #FFFFFF;
        }

        .loc-select-arrow {
          position: absolute;
          right: 13px;
          top: 50%;
          transform: translateY(-50%);
          color: #8C8095;
          pointer-events: none;
        }

        /* ── Quick Branch Pills ── */
        .loc-quick-branches {
          padding-top: 18px;
          border-top: 1px solid #F0EAE1;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .loc-quick-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: #8C8095;
        }

        .loc-pills-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .loc-branch-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background-color: #FAF7F2;
          border: 1px solid #E0D5C5;
          border-radius: 8px;
          padding: 8px 14px;
          font-size: 12.5px;
          font-weight: 600;
          color: #2D143E;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .loc-pill-num {
          font-family: 'Playfair Display', Georgia, serif;
          font-weight: 700;
          color: #C9A84C;
          font-size: 13px;
        }

        .loc-pill-arrow {
          color: #8C8095;
          transition: transform 0.2s ease, color 0.2s ease;
        }

        .loc-branch-pill:hover {
          background-color: #1E0038;
          color: #FFFFFF;
          border-color: #1E0038;
          transform: translateY(-1px);
        }

        .loc-branch-pill:hover .loc-pill-arrow {
          color: #C9A84C;
          transform: translateX(3px);
        }

        /* ── Responsive ── */
        @media (max-width: 640px) {
          .loc-clean-hero {
            padding: 38px 16px 32px;
          }
          .loc-clean-card {
            padding: 24px 20px 22px;
          }
          .loc-card-accent-bar {
            left: 20px;
            right: 20px;
          }
          .loc-form-grid {
            grid-template-columns: 1fr;
            gap: 14px;
          }
          .loc-pills-row {
            flex-direction: column;
          }
          .loc-branch-pill {
            width: 100%;
            justify-content: space-between;
          }
        }
      `}</style>
    </>
  );
}